import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, Calendar, FileSpreadsheet, Building, Users } from 'lucide-react';

export default function OfficeAdminPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.officeAdmin} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">Specialized Help</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Resume Help for Office & Administrative Jobs
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Administrative professionals need resumes that showcase organization, attention to detail, and the ability to keep operations running smoothly.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Common Problems */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Common Resume Problems for Admin Roles
          </h2>
          
          <div className="space-y-4">
            {[
              { problem: 'Listing tasks instead of impact', fix: 'We show how you improved efficiency, saved time, or streamlined processes.' },
              { problem: 'Underselling software skills', fix: 'We highlight proficiency in Microsoft Office, Google Workspace, scheduling tools, and more.' },
              { problem: 'Missing metrics and achievements', fix: 'We quantify your contributions—calendars managed, events coordinated, budgets handled.' },
              { problem: 'Generic descriptions that blend in', fix: 'We position you as the reliable backbone of office operations.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold">✕</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 mb-1">{item.problem}</p>
                    <p className="text-slate-600 text-sm">→ {item.fix}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* What Gets Improved */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
              What We Improve for Administrative Resumes
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Calendar, title: 'Scheduling & Coordination', desc: 'Highlight complex calendar management and event coordination skills.' },
                { icon: FileSpreadsheet, title: 'Software Proficiency', desc: 'Showcase Excel, Word, PowerPoint, Google Suite, and industry tools.' },
                { icon: Building, title: 'Office Operations', desc: 'Demonstrate your role in keeping the office running efficiently.' },
                { icon: Users, title: 'Communication Skills', desc: 'Show your ability to work with stakeholders at all levels.' },
                { icon: Check, title: 'Attention to Detail', desc: 'Highlight accuracy in data entry, proofreading, and documentation.' },
                { icon: Check, title: 'Problem Solving', desc: 'Show initiative in improving processes and solving day-to-day challenges.' },
              ].map((item, i) => (
                <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                  <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Roles We Help With */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Office & Admin Roles We Help With
          </h2>
          
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Administrative Assistant',
              'Executive Assistant',
              'Office Manager',
              'Receptionist',
              'Office Coordinator',
              'Data Entry Clerk',
              'Secretary',
              'Administrative Coordinator',
              'Front Desk Administrator',
              'Office Administrator',
              'Operations Assistant',
              'Virtual Assistant',
            ].map((role, i) => (
              <Badge key={i} className="bg-slate-100 text-slate-700 px-4 py-2 text-sm">{role}</Badge>
            ))}
          </div>
        </section>

        {/* Related Pages */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Explore More
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/for/customer-service" className="text-teal-700 hover:text-teal-800 underline">Customer Service Jobs</Link>
              <span className="text-slate-300">|</span>
              <Link to="/for/entry-level" className="text-teal-700 hover:text-teal-800 underline">Entry-Level Jobs</Link>
              <span className="text-slate-300">|</span>
              <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Resume Rewrite Service</Link>
              <span className="text-slate-300">|</span>
              <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View Pricing</Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready to improve your administrative resume?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and we'll review your resume personally. No payment until scope is confirmed.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
